import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { login, redirectToSignup, useClassicSiteAuth } from "../auth";
import SessionEnvironmentDiagnosticPanel from "./diagnostics/SessionEnvironmentDiagnosticPanel";
import { getEnvironmentSnapshot } from "../api/classicSiteEnvironmentDiagnostics";

function MemberLoadingState() {
  return (
    <div className="cs-auth-gate" role="status" aria-live="polite">
      <p className="cs-auth-gate__title">Checking session…</p>
      <p className="cs-auth-gate__sub">Verifying Django session via hoodie API.</p>
    </div>
  );
}

function MemberUnauthenticatedState({ status, message, returnPath, diagnostic }) {
  const isExpired = status === "expired";
  const { crossOrigin } = getEnvironmentSnapshot();
  return (
    <div className="cs-auth-gate">
      <p className="cs-auth-gate__title">
        {isExpired ? "Session expired" : "Sign in required"}
      </p>
      <p className="cs-auth-gate__sub">
        {isExpired
          ? "Your Django session is no longer active. Sign in again to continue."
          : "Member routes require a Django session. Sign in with your Mamahood email and password."}
      </p>
      {message ? <p className="cs-auth-gate__detail">{message}</p> : null}
      {crossOrigin ? (
        <SessionEnvironmentDiagnosticPanel diagnostic={diagnostic} compact />
      ) : null}
      <div className="cs-auth-gate__actions">
        <button type="button" className="cs-btn cs-btn--primary" onClick={() => login(returnPath)}>
          Sign in
        </button>
        <button
          type="button"
          className="cs-btn cs-btn--ghost"
          onClick={() => redirectToSignup(returnPath)}
        >
          Create account
        </button>
      </div>
    </div>
  );
}

function MemberErrorState({ message, onRetry, diagnostic }) {
  return (
    <div className="cs-auth-gate cs-auth-gate--error">
      <p className="cs-auth-gate__title">Session check failed</p>
      <p className="cs-auth-gate__sub">
        Could not verify authentication. The backend may be reachable in a direct
        browser tab but not from this React origin — see diagnostics below.
      </p>
      {message ? <p className="cs-auth-gate__detail">{message}</p> : null}
      <SessionEnvironmentDiagnosticPanel diagnostic={diagnostic} compact />
      <div className="cs-auth-gate__actions">
        <button type="button" className="cs-btn cs-btn--primary" onClick={onRetry}>
          Retry
        </button>
      </div>
    </div>
  );
}

function MemberUnconfiguredState({ message, diagnostic }) {
  return (
    <div className="cs-auth-gate cs-auth-gate--error">
      <p className="cs-auth-gate__title">Hoodie API not configured</p>
      <p className="cs-auth-gate__sub">
        Set <code>REACT_APP_HOODIE_API_BASE_URL</code> to the Django origin to
        verify sessions.
      </p>
      {message ? <p className="cs-auth-gate__detail">{message}</p> : null}
      <SessionEnvironmentDiagnosticPanel diagnostic={diagnostic} compact />
    </div>
  );
}

/**
 * Protects /classic-site/member/* — requires Django session for child routes.
 */
export default function MemberRouteGuard() {
  const location = useLocation();
  const { status, isLoading, error, refresh, lastDiagnostic } = useClassicSiteAuth();
  const returnPath = `${location.pathname}${location.search}`;

  if (isLoading) {
    return <MemberLoadingState />;
  }

  if (status === "unconfigured") {
    return (
      <MemberUnconfiguredState message={error} diagnostic={lastDiagnostic} />
    );
  }

  if (status === "error") {
    return (
      <MemberErrorState
        message={error}
        onRetry={refresh}
        diagnostic={lastDiagnostic}
      />
    );
  }

  if (status === "unauthenticated" || status === "expired") {
    return (
      <MemberUnauthenticatedState
        status={status}
        message={error}
        returnPath={returnPath}
        diagnostic={lastDiagnostic}
      />
    );
  }

  if (status === "authenticated") {
    return <Outlet />;
  }

  return <MemberLoadingState />;
}

/**
 * Compact session indicator for layout header.
 */
export function ClassicSiteSessionBadge() {
  const {
    status,
    isLoading,
    identity,
    refresh,
    login: signIn,
    logout: signOut,
  } = useClassicSiteAuth();

  if (isLoading) {
    return <span className="cs-session-badge cs-session-badge--loading">Session…</span>;
  }

  if (status === "authenticated" && identity) {
    return (
      <span className="cs-session-badge cs-session-badge--ok">
        {identity.displayName}
        {" · "}
        <button type="button" className="cs-link-btn" onClick={() => signOut()}>
          Sign out
        </button>
        {" · "}
        <button type="button" className="cs-link-btn" onClick={refresh}>
          Refresh
        </button>
      </span>
    );
  }

  if (status === "unauthenticated" || status === "expired") {
    return (
      <span className="cs-session-badge cs-session-badge--anon">
        Not signed in
        {" · "}
        <button type="button" className="cs-link-btn" onClick={() => signIn()}>
          Sign in
        </button>
      </span>
    );
  }

  if (status === "error" || status === "unconfigured") {
    return (
      <span className="cs-session-badge cs-session-badge--warn">
        Session unknown
        {" · "}
        <button type="button" className="cs-link-btn" onClick={refresh}>
          Retry
        </button>
      </span>
    );
  }

  return null;
}
